/**
 * Keeping score for klaverjassen played with real cards.
 *
 * A store of its own rather than a second shape inside `scorekeeper.ts`.
 *
 * The plan said to widen that store into a discriminated union. On opening it
 * that turned out to be the wrong call: every action there — `setBid`,
 * `confirmRound`, `reopenRound` and the rest — would have to narrow, and that
 * is working code holding the family's actual scores. A sibling store touches
 * none of it, and it is the same shape the rest of the app already has
 * (`useHearts` beside `useLive`, `HeartsGame` beside `LiveGame`).
 *
 * The one thing that has to be shared is the sync queue, and `flushSyncQueue`
 * now drains both.
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { Seat, Suit } from '@shared/types/game';
import type { KlaverjasDealEntry, KlaverjasScoreGame } from '@shared/types/klaverjas';
import { klaverjasTotals } from '@shared/types/klaverjas';
import {
  DEFAULT_KLAVERJAS_OPTIONS,
  DEAL_TOTAL,
  KLAVERJAS_PLAYERS,
  klaverjasFinished,
  scoreDealFromTotals,
  type KlaverjasOptions,
} from '@shared/game-logic';
import { newId } from '@/lib/id';

export interface NewKlaverjasInput {
  /** Exactly four, already in seat order: 0 and 2 are one team, 1 and 3 the other. */
  seats: Array<{ displayName: string; profileId: string | null }>;
  options: KlaverjasOptions;
  firstDealerSeat: number;
}

interface KlaverjasScoreData {
  games: Record<string, KlaverjasScoreGame>;
  lastGameId: string | null;
  pendingSync: string[];
}

interface KlaverjasScoreActions {
  createGame: (input: NewKlaverjasInput) => string;
  deleteGame: (gameId: string) => void;
  /** Enter or correct a deal. Anything left null keeps its current value. */
  setDeal: (
    gameId: string,
    dealNo: number,
    patch: Partial<Pick<KlaverjasDealEntry, 'playingTeam' | 'trump' | 'takenTeam0' | 'roem'>>
  ) => void;
  /** Score the deal and open the next one. */
  confirmDeal: (gameId: string, dealNo: number) => void;
  /** Reopen a scored deal to fix a typo; every total behind it recomputes. */
  reopenDeal: (gameId: string, dealNo: number) => void;
  markSynced: (gameId: string) => void;
  /** Put a potje from the server onto this device — see the note in `scorekeeper`. */
  adoptGame: (game: KlaverjasScoreGame) => void;
}

export type KlaverjasScoreStore = KlaverjasScoreData & KlaverjasScoreActions;

const EMPTY: KlaverjasScoreData = { games: {}, lastGameId: null, pendingSync: [] };

function blankDeal(dealNo: number, firstDealerSeat: number): KlaverjasDealEntry {
  return {
    dealNo,
    dealerSeat: (firstDealerSeat + dealNo - 1) % KLAVERJAS_PLAYERS,
    playingTeam: null,
    trump: null,
    takenTeam0: null,
    roem: [0, 0],
    status: 'entering',
    result: null,
  };
}

/** Ready to score: who played, which trump, and the one number. */
export function dealIsComplete(deal: KlaverjasDealEntry): boolean {
  return deal.playingTeam !== null && deal.trump !== null && deal.takenTeam0 !== null;
}

export const useKlaverjasScore = create<KlaverjasScoreStore>()(
  persist(
    (set) => ({
      ...EMPTY,

      createGame: (input) => {
        const id = newId();
        const now = new Date().toISOString();

        const seats: Seat[] = input.seats.map((seat, index) => ({
          seat: index,
          displayName: seat.displayName.trim(),
          profileId: seat.profileId,
        }));

        const game: KlaverjasScoreGame = {
          id,
          mode: 'scorekeeper',
          game: 'klaverjas',
          createdAt: now,
          updatedAt: now,
          finishedAt: null,
          status: 'in-progress',
          options: input.options,
          seats,
          deals: [blankDeal(1, input.firstDealerSeat)],
        };

        set((state) => ({
          games: { ...state.games, [id]: game },
          lastGameId: id,
        }));
        return id;
      },

      deleteGame: (gameId) =>
        set((state) => {
          const games = { ...state.games };
          delete games[gameId];
          return {
            games,
            lastGameId: state.lastGameId === gameId ? null : state.lastGameId,
            pendingSync: state.pendingSync.filter((id) => id !== gameId),
          };
        }),

      setDeal: (gameId, dealNo, patch) =>
        set((state) => {
          const game = state.games[gameId];
          if (!game) return {};

          const deals = game.deals.map((deal) =>
            deal.dealNo === dealNo ? { ...deal, ...patch } : deal
          );
          return { games: { ...state.games, [gameId]: touch({ ...game, deals }) } };
        }),

      confirmDeal: (gameId, dealNo) =>
        set((state) => {
          const game = state.games[gameId];
          if (!game) return {};

          const deal = game.deals.find((entry) => entry.dealNo === dealNo);
          if (!deal || !dealIsComplete(deal)) return {};

          const taken: [number, number] = [deal.takenTeam0!, DEAL_TOTAL - deal.takenTeam0!];
          const result = scoreDealFromTotals({
            playingTeam: deal.playingTeam!,
            taken,
            roem: deal.roem,
            pitBonus: game.options.pitBonus,
          });

          // `raw` is working detail from the rule engine; only the three fields
          // the score sheet shows are stored.
          const stored = { points: result.points, nat: result.nat, pitTeam: result.pitTeam };
          const deals = game.deals.map((entry) =>
            entry.dealNo === dealNo ? { ...entry, status: 'done' as const, result: stored } : entry
          );

          const totals = klaverjasTotals({ ...game, deals });
          const done = klaverjasFinished(totals, deals.length, game.options.length);

          /*
           * The next deal opens straight away unless the game is over, so the
           * table never has to press anything to keep going. The dealer rotates
           * from whoever dealt the first one — `blankDeal` does that sum, so it
           * needs the *first* dealer seat and not the last.
           */
          const withNext = done
            ? deals
            : [...deals, blankDeal(deals.length + 1, deals[0].dealerSeat)];

          const updated = touch({
            ...game,
            deals: withNext,
            status: done ? ('finished' as const) : ('in-progress' as const),
            finishedAt: done ? new Date().toISOString() : null,
          });

          return {
            games: { ...state.games, [gameId]: updated },
            pendingSync: queue(state.pendingSync, gameId),
          };
        }),

      reopenDeal: (gameId, dealNo) =>
        set((state) => {
          const game = state.games[gameId];
          if (!game) return {};

          /*
           * Drop everything after the deal being corrected. Totals are a running
           * sum, so a deal changed in the middle would otherwise leave every
           * later line agreeing with a number that no longer exists.
           */
          const deals = game.deals
            .filter((entry) => entry.dealNo <= dealNo)
            .map((entry) =>
              entry.dealNo === dealNo
                ? { ...entry, status: 'entering' as const, result: null }
                : entry
            );

          return {
            games: {
              ...state.games,
              [gameId]: touch({
                ...game,
                deals,
                status: 'in-progress',
                finishedAt: null,
              }),
            },
            pendingSync: queue(state.pendingSync, gameId),
          };
        }),

      markSynced: (gameId) =>
        set((state) => ({ pendingSync: state.pendingSync.filter((id) => id !== gameId) })),

      adoptGame: (game) =>
        set((state) => ({
          games: { ...state.games, [game.id]: game },
          lastGameId: game.id,
        })),
    }),
    {
      name: 'boerenbridge.klaverjas',
      version: 1,
      storage: createJSONStorage(() => localStorage),
    }
  )
);

function touch(game: KlaverjasScoreGame): KlaverjasScoreGame {
  return { ...game, updatedAt: new Date().toISOString() };
}

function queue(pending: string[], gameId: string): string[] {
  return pending.includes(gameId) ? pending : [...pending, gameId];
}

// --- selectors -------------------------------------------------------------

export const selectKlaverjasList = (state: KlaverjasScoreStore): KlaverjasScoreGame[] =>
  Object.values(state.games).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

export function selectKlaverjasGame(gameId: string | undefined) {
  return (state: KlaverjasScoreStore): KlaverjasScoreGame | undefined =>
    gameId ? state.games[gameId] : undefined;
}

export function currentDealOf(game: KlaverjasScoreGame): KlaverjasDealEntry | undefined {
  return game.deals.find((deal) => deal.status === 'entering');
}

export const DEFAULT_NEW_KLAVERJAS: Omit<NewKlaverjasInput, 'seats'> = {
  options: DEFAULT_KLAVERJAS_OPTIONS,
  firstDealerSeat: 0,
};

export type { Suit };
